import React from "react";
import { Anchor, Calculator, Clock, Fuel, Gauge, Info, Ruler, Ship, Wind } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import CalculatorField from "@/components/public/calculators/CalculatorField";
import { getCalculatorToolRuntimeCopy } from "./calculatorTranslations";

const toolConfig = {
  "fuel-calculator": {
    icon: Fuel,
    defaults: { distance: "24", speed: "6", burn: "8" },
    fields: [
      ["distance", "Distance", "NM", "Total passage distance in nautical miles.", "24"],
      ["speed", "Average speed", "kn", "Use a conservative average speed, not your best speed.", "6"],
      ["burn", "Fuel burn", "L/h", "Your engine fuel consumption per hour.", "8"],
    ],
    calculate: (v) => {
      const hours = Number(v.distance || 0) / Math.max(Number(v.speed || 0), 0.1);
      return { main: `${(hours * Number(v.burn || 0)).toFixed(1)} L`, hours, miles: 0 };
    },
  },
  "eta-calculator": {
    icon: Clock,
    defaults: { distance: "24", speed: "6" },
    fields: [
      ["distance", "Distance", "NM", "Total route length in nautical miles.", "24"],
      ["speed", "Average speed", "kn", "Expected speed over ground after current, wind, and sea state.", "6"],
    ],
    calculate: (v) => {
      const hours = Number(v.distance || 0) / Math.max(Number(v.speed || 0), 0.1);
      const whole = Math.floor(hours);
      const minutes = Math.round((hours - whole) * 60);
      return { main: `${whole}h ${minutes}m`, hours, miles: 0 };
    },
  },
  "distance-calculator": {
    icon: Ruler,
    defaults: { miles: "12" },
    fields: [["miles", "Nautical distance", "NM", "Enter the charted nautical miles you want to convert.", "12"]],
    calculate: (v) => ({ main: `${(Number(v.miles || 0) * 1.852).toFixed(1)} km`, hours: 0, miles: Number(v.miles || 0) }),
  },
  "anchor-scope": {
    icon: Anchor,
    defaults: { depth: "6", freeboard: "1", ratio: "5" },
    fields: [
      ["depth", "Water depth", "m", "Depth at high water where the anchor sits.", "6"],
      ["freeboard", "Bow height", "m", "Height from waterline to bow roller.", "1"],
      ["ratio", "Scope ratio", ":1", "Common range is 4:1 to 7:1 depending on conditions.", "5"],
    ],
    calculate: (v) => ({ main: `${((Number(v.depth || 0) + Number(v.freeboard || 0)) * Number(v.ratio || 0)).toFixed(1)} m`, hours: 0, miles: 0 }),
  },
  "apparent-wind": {
    icon: Wind,
    defaults: { trueWind: "14", boatSpeed: "6", angle: "60" },
    fields: [
      ["trueWind", "True wind speed", "kn", "Forecast or measured true wind speed.", "14"],
      ["boatSpeed", "Boat speed", "kn", "Current boat speed through the water.", "6"],
      ["angle", "True wind angle", "°", "Angle between bow and true wind direction.", "60"],
    ],
    calculate: (v) => {
      const tw = Number(v.trueWind || 0);
      const bs = Number(v.boatSpeed || 0);
      const rad = Number(v.angle || 0) * Math.PI / 180;
      const apparent = Math.sqrt((tw * Math.cos(rad) + bs) ** 2 + (tw * Math.sin(rad)) ** 2);
      return { main: `${apparent.toFixed(1)} kn`, hours: 0, miles: 0 };
    },
  },
  "hull-speed": {
    icon: Gauge,
    defaults: { lwl: "30" },
    fields: [["lwl", "Waterline length", "ft", "Use the boat length at the waterline, not necessarily LOA.", "30"]],
    calculate: (v) => ({ main: `${(1.34 * Math.sqrt(Number(v.lwl || 0))).toFixed(1)} kn`, hours: 0, miles: 0 }),
  },
};

const calculatorCopy = {
  en: {
    badge: "Nautical calculator",
    guidance: "Hover the help icons to understand each value. Use conservative numbers and verify onboard before making decisions.",
    result: "Result",
    formula: "Formula",
    details: {
      "fuel-calculator": (result) => `${result.hours.toFixed(1)} engine hours before reserve.`,
      "eta-calculator": (result) => `${result.hours.toFixed(1)} total hours underway.`,
      "distance-calculator": (result) => `${(result.miles * 1.15078).toFixed(1)} statute miles.`,
      "anchor-scope": () => "Recommended rode length before adding extra safety margin.",
      "apparent-wind": () => "Estimated apparent wind felt onboard.",
      "hull-speed": () => "Classic displacement hull speed estimate.",
    },
  },
  he: {
    badge: "מחשבון שייט",
    guidance: "עברו על סמלי העזרה כדי להבין כל ערך. השתמשו במספרים שמרניים ואמתו את הנתונים על הסירה לפני קבלת החלטות.",
    result: "תוצאה",
    formula: "נוסחה",
    fields: {
      "fuel-calculator": [
        ["distance", "מרחק הפלגה", "מייל ימי", "מרחק ההפלגה הכולל במיילים ימיים.", "24"],
        ["speed", "מהירות ממוצעת", "קשר", "השתמשו במהירות שמרנית, לא במהירות הטובה ביותר שלכם.", "6"],
        ["burn", "צריכת דלק", "ל׳/שעה", "צריכת הדלק של המנוע לשעה.", "8"],
      ],
      "eta-calculator": [
        ["distance", "מרחק", "מייל ימי", "אורך המסלול הכולל במיילים ימיים.", "24"],
        ["speed", "מהירות ממוצעת", "קשר", "מהירות צפויה על פני הקרקע אחרי זרם, רוח ומצב ים.", "6"],
      ],
      "distance-calculator": [["miles", "מרחק ימי", "מייל ימי", "הזינו את המרחק הימי מהמפה שברצונכם להמיר.", "12"]],
      "anchor-scope": [
        ["depth", "עומק מים", "מ׳", "העומק בגאות גבוהה במקום שבו העוגן מונח.", "6"],
        ["freeboard", "גובה חרטום", "מ׳", "הגובה מקו המים עד גלגלת החרטום.", "1"],
        ["ratio", "יחס עגינה", ":1", "טווח נפוץ הוא 4:1 עד 7:1 בהתאם לתנאים.", "5"],
      ],
      "apparent-wind": [
        ["trueWind", "מהירות רוח אמיתית", "קשר", "מהירות רוח אמיתית לפי תחזית או מדידה.", "14"],
        ["boatSpeed", "מהירות הסירה", "קשר", "מהירות הסירה הנוכחית במים.", "6"],
        ["angle", "זווית רוח אמיתית", "°", "הזווית בין החרטום לכיוון הרוח האמיתית.", "60"],
      ],
      "hull-speed": [["lwl", "אורך קו המים", "רגל", "השתמשו באורך הסירה בקו המים, לא בהכרח באורך הכולל.", "30"]],
    },
    details: {
      "fuel-calculator": (result) => `${result.hours.toFixed(1)} שעות מנוע לפני הוספת עתודת דלק.`,
      "eta-calculator": (result) => `${result.hours.toFixed(1)} שעות הפלגה בסך הכול.`,
      "distance-calculator": (result) => `${(result.miles * 1.15078).toFixed(1)} מיילים יבשתיים.`,
      "anchor-scope": () => "אורך חבל/שרשרת מומלץ לפני הוספת מרווח בטיחות נוסף.",
      "apparent-wind": () => "הערכת הרוח המדומה שמרגישים על הסירה.",
      "hull-speed": () => "הערכת מהירות גוף קלאסית לסירת הדחק.",
    },
  },
};

export default function NauticalToolCalculator({ tool, lang = "en" }) {
  const config = toolConfig[tool.slug] || toolConfig["fuel-calculator"];
  const runtimeCopy = getCalculatorToolRuntimeCopy(tool.slug, lang);
  const copy = runtimeCopy.common;
  const fields = runtimeCopy.fields;
  const [values, setValues] = React.useState(config.defaults);
  const result = React.useMemo(() => config.calculate(values), [config, values]);
  const detail = runtimeCopy.detail(result);
  const localizedTool = { ...tool, ...runtimeCopy.tool };
  const Icon = config.icon;

  React.useEffect(() => setValues(config.defaults), [config]);

  return (
    <article className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-xl">
      <div className="grid gap-6 bg-gradient-to-br from-ocean via-atlantic to-primary p-6 text-white md:grid-cols-[1fr_auto] md:p-10">
        <div>
          <Badge className="bg-white/15 text-white hover:bg-white/15">{copy.badge}</Badge>
          <h1 className="mt-4 font-heading text-4xl font-black md:text-6xl">{localizedTool.title}</h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-cyan-50/90">{localizedTool.description}</p>
        </div>
        <div className="grid h-24 w-24 place-items-center rounded-3xl bg-white/15 ring-1 ring-white/25">
          <Icon className="h-12 w-12" />
        </div>
      </div>

      <div className="grid gap-6 p-5 md:p-8 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-2xl bg-primary/10 p-4 text-sm text-foreground">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <p>{copy.guidance}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {fields.map(([key, label, unit, tooltip, placeholder]) => (
              <CalculatorField key={key} label={label} unit={unit} tooltip={tooltip} placeholder={placeholder} value={values[key] || ""} onChange={(value) => setValues((prev) => ({ ...prev, [key]: value }))} />
            ))}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-3xl bg-ocean p-6 text-white shadow-xl">
            <div className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-cyan-100"><Calculator className="h-4 w-4" /> {copy.result}</div>
            <p className="mt-4 text-4xl font-black">{result.main}</p>
            <p className="mt-2 text-sm leading-6 text-cyan-50/85">{detail}</p>
          </div>
          <div className="rounded-3xl border border-border bg-background p-5">
            <h2 className="flex items-center gap-2 text-lg font-black"><Ship className="h-5 w-5 text-primary" /> {copy.formula}</h2>
            <p className="mt-2 text-muted-foreground">{runtimeCopy.formula}</p>
          </div>
        </aside>
      </div>
    </article>
  );
}